import { css } from '@emotion/react';

const GlobalStyle = css`
  /*  --------------------------------------------------------------------------------
  --- FONTS
  -------------------------------------------------------------------------------- */
  @font-face {
    font-family: Roboto;
    src: url('/fonts/Roboto-Regular.woff2');
    font-weight: 400;
  }

  @font-face {
    font-family: Roboto;
    src: url('/fonts/Roboto-Medium.woff2');
    font-weight: 500;
  }

  @font-face {
    font-family: Roboto;
    src: url('/fonts/Roboto-Bold.woff2');
    font-weight: 600 700;
  }

  /*  --------------------------------------------------------------------------------
  --- RESET
  -------------------------------------------------------------------------------- */
  /*
  http://meyerweb.com/eric/tools/css/reset/
     v2.0 | 20110126
     License: none (public domain)
    */
  html,
  body,
  div,
  span,
  applet,
  object,
  iframe,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  blockquote,
  pre,
  a,
  abbr,
  acronym,
  address,
  big,
  cite,
  code,
  del,
  dfn,
  em,
  img,
  ins,
  kbd,
  q,
  s,
  samp,
  small,
  strike,
  strong,
  sub,
  sup,
  tt,
  var,
  b,
  u,
  i,
  center,
  dl,
  dt,
  dd,
  ol,
  ul,
  li,
  fieldset,
  form,
  label,
  legend,
  table,
  caption,
  tbody,
  tfoot,
  thead,
  tr,
  th,
  td,
  article,
  aside,
  canvas,
  details,
  embed,
  figure,
  figcaption,
  footer,
  header,
  hgroup,
  menu,
  nav,
  output,
  ruby,
  section,
  summary,
  time,
  mark,
  audio,
  video {
    margin: 0;
    padding: 0;
    border: 0;
    font-size: 100%;
    font: inherit;
    vertical-align: baseline;
  }
  /* HTML5 display-role reset for older browsers */
  article,
  aside,
  details,
  figcaption,
  figure,
  footer,
  header,
  hgroup,
  menu,
  nav,
  section {
    display: block;
  }
  body {
    line-height: 1;
    min-width: 320px;
  }
  ol,
  ul {
    list-style: none;
  }
  blockquote,
  q {
    quotes: none;
  }
  blockquote:before,
  blockquote:after,
  q:before,
  q:after {
    content: '';
    content: none;
  }
  table {
    border-collapse: collapse;
    border-spacing: 0;
  }
  a {
    text-decoration: none;
  }

  /*  --------------------------------------------------------------------------------
  --- VAR
  -------------------------------------------------------------------------------- */
  :root {
    /* Font */
    --default-font-family: 'Helvetica', sans-serif;

    /* Size */
    --width-content-min: 600px;
    --width-content-max: 100%;
    --width-sidebar: 200px;
    --width-side-panel-regular: 400px;
    --width-side-panel-big: 800px;
    --height-header: 72px;
    --width-modal: 500px;

    /* Z-indexes */
    --z-index-content: 300;
    --z-index-sidebar: 400;
    --z-index-header: 450;
    --z-index-side-panel: 500;
    --z-index-toast: 700;
    --z-index-modal: 999;
  }

  /*  --------------------------------------------------------------------------------
  --- GLOBAL
  -------------------------------------------------------------------------------- */
  html {
    height: 100%;
    font-weight: 400;
    font-size: 15px;
    font-feature-settings: 'lnum';
    box-sizing: border-box;
  }

  body {
    margin: 0;
    padding: 0;
    font-family: 'Roboto', sans-serif;
  }

  /* --------------------------------------------------------------------------------
     SCROLLBAR: hide track background and border, only show the thumb
  -------------------------------------------------------------------------------- */
  /* Chrome, Edge, Safari */
  ::-webkit-scrollbar {
    width: 12px; /* adjust thickness */
    background: transparent; /* no background on the container */
  }
  ::-webkit-scrollbar-track {
    background: transparent; /* hide the track */
  }
  ::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.4); /* your thumb color */
    border: none; /* no border */
    border-radius: 6px; /* rounded corners */
  }
  ::-webkit-scrollbar-corner {
    background: transparent; /* corner where two scrollbars meet */
  }

  /* Firefox */
  * {
    scrollbar-width: thin; /* use thin scrollbar */
    scrollbar-color: rgba(0, 0, 0, 0.4) transparent; /* thumb and track */
  }

  /* --------------------------------------------------------------------------------
     TOAST
  -------------------------------------------------------------------------------- */
  .Toastify__toast {
    max-width: none !important;
    width: 420px !important;
    margin-right: 400px !important;
  }

  .Toastify__toast-container--bottom-right {
    right: 132px !important;
  }

  .Toastify__progress-bar {
    border-radius: 0 !important; /* Remove rounded corners */
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  /*  --------------------------------------------------------------------------------
--- OVERRIDE react-multi-date-picker's popup
-------------------------------------------------------------------------------- */
  .rmdp-calendar,
  .rmdp-calendar * {
    font-family: 'Roboto', sans-serif !important;
  }

  .rmdp-calendar {
    background-color: white !important;
    border-radius: 4px;
  }

  /* Use primary blue for header & navigation, neutral for weekdays */
  .rmdp-header-values {
    color: var(--joy-palette-bluePrimary);
  }

  .rmdp-arrow {
    border-color: var(--joy-palette-bluePrimary) !important;
    margin-top: 0px !important;
  }

  /* Arrow container: neutral by default, primary blue only on our terms */
  .rmdp-arrow-container {
    background-color: transparent !important;
    box-shadow: none !important;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
  }

  .rmdp-arrow-container:hover {
    background-color: var(--joy-palette-blueLight) !important;
    box-shadow: none !important;
  }

  /* Weekday labels (Sun, Mon, ...) use design grey, not any blue */
  .rmdp-week-day,
  .rmdp-week-day span {
    color: var(--joy-palette-grey5) !important;
  }

  /* Base day styles: neutral text, no background or shadow */
  .rmdp-day span {
    color: var(--joy-palette-black);
    background-color: transparent;
    box-shadow: none;
  }

  /* Range days: grey background, but keep blue circle around number */
  .rmdp-day.rmdp-range {
    background-color: transparent !important;
  }

  .rmdp-day.rmdp-range span:not(.highlight) {
    background-color: var(--joy-palette-bluePrimary) !important;
    color: white !important;
    box-shadow: none !important;
  }

  /* Selected / start / end days: primary blue, no shadow */
  .rmdp-day.rmdp-selected span:not(.highlight),
  .rmdp-day.rmdp-start span:not(.highlight),
  .rmdp-day.rmdp-end span:not(.highlight) {
    background-color: var(--joy-palette-bluePrimary) !important;
    color: white !important;
    box-shadow: none !important;
  }

  /* Hover state: lighter blue, still shadowless */
  .rmdp-day:not(.rmdp-day-hidden) span:hover {
    background-color: var(--joy-palette-blueLight) !important;
    color: var(--joy-palette-black) !important;
    box-shadow: none !important;
  }

  /* Ensure no drop shadow is rendered under the selected item */
  .rmdp-range,
  .rmdp-day.rmdp-selected span,
  .rmdp-day.rmdp-start span,
  .rmdp-day.rmdp-end span {
    box-shadow: none !important;
  }
`;

export default GlobalStyle;
